import { ApiError, apiRequest } from '@/lib/queryClient';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { type InterpreterRecord, recordRequest } from './api';
import type {
  InterpreterRecordContext,
  InterpreterSummaryOptions,
  InterpreterSummaryResult,
} from './interpreter.types';

// Persisted recording and note state, independent of microphone/socket capture.
export const useInterpreterRecord = (
  id: string | undefined,
  { active, setPhase, setMessage }: InterpreterRecordContext
) => {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['interpreter', id],
    queryFn: async () => {
      try {
        return await recordRequest('GET', `/${id}`);
      } catch (error) {
        if (error instanceof ApiError && [401, 403, 404].includes(error.status)) return null;
        throw error;
      }
    },
    gcTime: 0,
    enabled: Boolean(id),
    retry: false,
  });
  const [summary, setSummary] = useState('');
  const clearSummary = useCallback(() => setSummary(''), []);
  const refresh = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['interpreter', id] });
  }, [id, queryClient]);
  const store = (record: InterpreterRecord) => queryClient.setQueryData(['interpreter', id], record);
  const summarize = async (options: InterpreterSummaryOptions = {}): Promise<InterpreterSummaryResult | undefined> => {
    if (!id || active.current) return;
    active.current = true;
    setPhase('summarizing');
    setMessage('');
    try {
      await recordRequest('POST', `/${id}/pause`, {});
      const response: InterpreterSummaryResult = await (
        await apiRequest('POST', `/api/interpreter/${id}/summary`, options)
      ).json();
      if (!response.success || !response.summary) throw new Error('summary_failed');
      setSummary(response.summary);
      return response;
    } catch {
      setMessage('Ekki tókst að búa til nótu. Vistaður texti er enn aðgengilegur. Reyndu aftur.');
    } finally {
      active.current = false;
      setPhase('idle');
    }
  };

  return { query, refresh, store, summary, clearSummary, summarize };
};
